import { UnknownAction } from "redux"
import type { ObjectsState } from "./reducer"
import type * as Types from './types'
import { updateObjectsAction, updateObjectsPassively } from "./actions"
import { Draft } from "@reduxjs/toolkit"
import { add, div, sub } from "~/lib/segment-patchup"
import { findSpecificObjectById } from "./selectors"
import type { Vector3Like } from "three"

function updateDelta(state: Draft<Record<string, Types.UtilityBox>>, delta: Vector3Like, affected: (item: Types.UtilityBox) => boolean) {
  for (const key in state) {
    const box = state[key]
    if (!affected(box)) {
      continue
    }

    box.position = add(box.position, delta)
  }
}

function updateWallSegment(state: Draft<Record<string, Types.UtilityBox>>, previous: Types.WallSegment, incoming: Types.PartialObject<Types.WallSegment>) {
  if (incoming.startPoint === undefined && incoming.endPoint === undefined) {
    return
  }

  const startPoint = incoming.startPoint ?? previous.startPoint
  const endPoint = incoming.endPoint ?? previous.endPoint
  const midPoint = div(add(startPoint, endPoint), 2)

  const previousStartPoint = previous.startPoint
  const previousEndPoint = previous.endPoint
  const previousMidPoint = div(add(previousStartPoint, previousEndPoint), 2)

  const delta = sub(midPoint, previousMidPoint)

  updateDelta(state, delta, item => item.wallSegmentId === incoming.id)
}

function updateWall(state: Draft<Record<string, Types.UtilityBox>>, objectsState: ObjectsState, previous: Types.Wall, incoming: Types.PartialObject<Types.Wall> | Types.PartialObject<Types.DenormalizedWall>) {
  if (incoming.segments !== undefined && incoming.segments.every(segment => typeof segment !== 'string')) {
    for (const segment of incoming.segments) {
      const prevSegment = findSpecificObjectById(objectsState, 'segments', segment.id)
      if (prevSegment !== undefined) {
        updateWallSegment(state, prevSegment, segment)
      }
    }
  }
}

export default function utilityBoxReducer(state: Draft<Record<string, Types.UtilityBox>>, objectsState: ObjectsState, action: UnknownAction) {
  switch (true) {
    case updateObjectsPassively.match(action):
    case updateObjectsAction.match(action):
      for (const obj of action.payload) {
        const prevSegment = findSpecificObjectById(objectsState, 'segments', obj.id)
        if (prevSegment !== undefined) {
          updateWallSegment(state, prevSegment, obj)
        }
        const prevWall = findSpecificObjectById(objectsState, 'objects', obj.id)
        if (prevWall !== undefined) {
          updateWall(state, objectsState, prevWall, obj)
        }
      }

      return
  }
}